import { useCallback } from 'react';
import { ace_hooks, assignment_hooks } from '@axo/shared/data-access/hooks';
import { useShouldUseAssignmentsVersion } from './useShouldUseAssignmentsVersion';

type UseMarkAsProcessedParams = {
  applicationID: string;
  marketCountry: string;
};

type UseMarkAsProcessedResult = {
  markAsProcessed: () => Promise<void>;
  isProcessing: boolean;
};

export const useMarkAsProcessed = ({
  applicationID,
  marketCountry,
}: UseMarkAsProcessedParams): UseMarkAsProcessedResult => {
  const version = useShouldUseAssignmentsVersion({
    applicationID,
    marketCountry,
  });

  const markAsProcessedV3 = ace_hooks.useSetAssignmentApplicationProcessedV3();
  const processBucket = ace_hooks.useProcessApplication();
  const processLegacy = assignment_hooks.useSetProcessed();

  const markAsProcessed = useCallback(async () => {
    if (version === 'v3') {
      await markAsProcessedV3.mutateAsync({ ApplicationID: applicationID });
    } else if (version === 'buckets') {
      await processBucket.mutateAsync({ applicationID });
    } else {
      await processLegacy.mutateAsync(applicationID);
    }
  }, [version, applicationID, markAsProcessedV3, processBucket, processLegacy]);

  const isProcessing =
    version === 'v3'
      ? markAsProcessedV3.isLoading
      : version === 'buckets'
        ? processBucket.isLoading
        : processLegacy.isLoading;

  return { markAsProcessed, isProcessing };
};
